import { FC, useEffect, useState } from "react";
import { Col, OverlayTrigger, Row, Tooltip } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faLink,
    faPause,
    faPlay,
    faUpRightFromSquare
} from "@fortawesome/free-solid-svg-icons";
import {
    NetworkBuild,
    TempLink,
    TempLinkVariables
} from "../../../models/types";
import { useLazyQuery } from "@apollo/client";
import { GET_TEMP_LINK } from "../../../graphql/queries";
import styles from "./AnalyticsBuildPreview.module.scss";

interface Props {
    networkBuild: NetworkBuild;
    globalPlay?: boolean;
}

export const AnalyticsBuildPreviewSimple: FC<Props> = ({
    networkBuild,
    globalPlay
}) => {
    const [play, updatePlay] = useState(true);
    const [iFrameContent, setIFrameContent] = useState<string | undefined>(
        undefined
    );

    // @ts-ignore
    const [getTempLink, { data: { generateLink } = {} }] = useLazyQuery<
        TempLink,
        TempLinkVariables
    >(GET_TEMP_LINK, {
        fetchPolicy: "no-cache",
        refetchWritePolicy: "overwrite"
    });

    useEffect(() => {
        if (globalPlay !== undefined) {
            updatePlay(globalPlay);
        }
    }, [globalPlay]);

    useEffect(() => {
        if (networkBuild && networkBuild.build) {
            const key =
                `${networkBuild.build.reportData.info.prefix}/${networkBuild.filename}`.replace(
                    "//",
                    "/"
                );
            getTempLink({
                variables: {
                    productId:
                        networkBuild?.build?.revision?.project?.product?.id ||
                        -1,
                    key: key
                }
            });
        }
    }, []);

    useEffect(() => {
        const fetchHtmlFromS3 = async (generateLink: string) => {
            try {
                const response = await fetch(generateLink);
                if (!response.ok) {
                    console.log(
                        "[DEBUG] Failed to fetch the HTML file from S3, no build yet or a failed build"
                    );
                    return;
                }

                const htmlData = await response.text();
                setIFrameContent(htmlData);
            } catch (error) {
                console.error("Error fetching the file:", error);
            }
        };

        if (generateLink && generateLink.link) {
            fetchHtmlFromS3(generateLink.link);
        }
    }, [generateLink]);

    return (
        <>
            {networkBuild && networkBuild.build ? (
                <>
                    <Row className="py-2">
                        <Col>
                            <OverlayTrigger
                                placement="top"
                                delay={{ show: 500, hide: 100 }}
                                overlay={
                                    <Tooltip>
                                        {play
                                            ? "Stop playing"
                                            : "Start playing"}
                                    </Tooltip>
                                }
                            >
                                <FontAwesomeIcon
                                    onClick={() => updatePlay(!play)}
                                    icon={play ? faPause : faPlay}
                                    className="mouseHover pb-2"
                                    size="xl"
                                />
                            </OverlayTrigger>
                        </Col>
                        <Col className="text-end pe-2 ms-auto">
                            <OverlayTrigger
                                placement="top"
                                delay={{ show: 500, hide: 200 }}
                                overlay={
                                    <Tooltip>Copy link to the playable</Tooltip>
                                }
                            >
                                <FontAwesomeIcon
                                    icon={faLink}
                                    className="mouseHover text-inverse px-1 me-2 btn btn-sm"
                                    onClick={async () => {
                                        if (generateLink?.link) {
                                            try {
                                                await navigator.clipboard.writeText(
                                                    generateLink.link
                                                );
                                            } catch (error: any) {
                                                console.error(error.message);
                                            }
                                        }
                                    }}
                                />
                            </OverlayTrigger>

                            <OverlayTrigger
                                placement="top"
                                delay={{ show: 500, hide: 200 }}
                                overlay={
                                    <Tooltip>
                                        Open the playable in a new tab
                                    </Tooltip>
                                }
                            >
                                <FontAwesomeIcon
                                    onClick={() => {
                                        if (generateLink?.link) {
                                            const win = window.open(
                                                generateLink.link,
                                                "_blank"
                                            );
                                            if (win !== null) {
                                                win.focus();
                                            }
                                        }
                                    }}
                                    className="btn btn-sm text-inverse px-1"
                                    icon={faUpRightFromSquare}
                                />
                            </OverlayTrigger>
                        </Col>
                    </Row>
                    <Row>
                        {play ? (
                            <div className={styles.iframeContainer}>
                                <iframe
                                    className={styles.responsiveIFrame}
                                    title="Playable Preview"
                                    srcDoc={iFrameContent}
                                    allow="autoplay 'none'; microphone 'none'"
                                />
                            </div>
                        ) : (
                            <div className={styles.iframePlaceholder}>
                                <div className={styles.previewText}>
                                    Preview window
                                </div>
                            </div>
                        )}
                    </Row>
                </>
            ) : (
                <div className="p-3 text-inverse-50 medium-font">
                    No builds to show. <br />
                </div>
            )}
        </>
    );
};
